import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import Autocomplete from '@embracesbs/component-autocomplete'
import Modal from '@embracesbs/component-modal'
import EmptyState from '@embracesbs/component-emptystate'
import Toast, { customToast } from '@embracesbs/component-toast'
import { useApi } from '../Hooks'
import { AuthRoleContext } from '../Components/Auth/AuthRoleContext'
import LoadingState from '../Components/Common/LoadingState'
import BandBackButton from '../Components/Common/BandBackButton'
import {
  CertificateIssuer,
  CertificateOverview,
  CertificateRequestState,
  CertificateTenantRef,
  ClusterCertificate,
  ClusterCertificateRequest
} from '../Interfaces/Types'

const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

// Not '': the Autocomplete treats an empty value as unset and picks an option itself.
const ALL_ISSUERS = '__all__'
const DAY_MS = 24 * 60 * 60 * 1000
// A certificate this close to expiry without renewing is worth a look.
const EXPIRY_WARNING_DAYS = 14

const bandStyle: CSSProperties = {
  flexShrink: 0,
  height: '52px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0 16px',
  backgroundColor: 'var(--tma-band-bg)'
}

const toolbarStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  background: 'var(--tma-panel-bg)',
  borderBottom: '1px solid var(--tma-list-card-border)',
  padding: '10px 16px'
}

const thStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--tma-card-bg)',
  borderBottom: '1px solid var(--tma-list-card-border)',
  padding: '8px 12px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--tma-text-strong)'
}

const tdStyle: CSSProperties = {
  borderBottom: '1px solid var(--tma-card-border)',
  padding: '10px 12px',
  fontSize: '13px',
  verticalAlign: 'top',
  color: 'var(--tma-text-strong)'
}

const monoStyle: CSSProperties = { fontFamily: MONO_FONT, fontSize: '12px', color: 'var(--tma-text-muted)', wordBreak: 'break-all' }
const mutedStyle: CSSProperties = { color: 'var(--tma-text-muted)', fontSize: '12px' }

// Status tokens so the accessibility themes can substitute hues (index.css).
// These are var() strings: derive tints with color-mix, never hex suffixes.
const pill = (color: string): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color,
  background: `color-mix(in srgb, ${color} 12%, transparent)`,
  borderRadius: '999px',
  padding: '2px 8px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  border: 'none',
  fontFamily: APP_FONT
})

// A filter chip: filled when selected.
const chip = (selected: boolean): CSSProperties => ({
  ...pill(selected ? 'var(--tma-accent)' : 'var(--tma-text-muted)'),
  background: selected ? 'color-mix(in srgb, var(--tma-accent) 14%, transparent)' : 'transparent',
  border: `1px solid ${selected ? 'var(--tma-accent)' : 'var(--tma-card-border)'}`,
  padding: '4px 10px',
  cursor: 'pointer'
})

const REQUEST_STATE: Record<CertificateRequestState, { label: string; color: string }> = {
  Issued: { label: 'Issued', color: 'var(--tma-status-ok-strong)' },
  Pending: { label: 'Pending', color: 'var(--tma-warning-text)' },
  Failed: { label: 'Failed', color: 'var(--tma-danger-text)' },
  Denied: { label: 'Denied', color: 'var(--tma-danger-text)' }
}

type View = 'requests' | 'certificates'
type RequestFilter = 'all' | 'failed' | 'pending' | 'issued'
type CertificateFilter = 'all' | 'notReady' | 'expiring'
type Sort = 'newest' | 'oldest'

const apiError = (error: any, fallback: string): string => {
  const message = error?.response?.data ?? error?.data
  return typeof message === 'string' && message.trim() && message.length <= 400 ? message : fallback
}

const time = (iso?: string | null): number => (iso ? new Date(iso).getTime() : 0)

const formatDateTime = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : '')

// "3 days ago", "in 20 days": enough to scan a column; the exact time is in the tooltip.
const relative = (iso?: string | null): string => {
  if (!iso) return ''
  const diff = new Date(iso).getTime() - Date.now()
  const abs = Math.abs(diff)
  const [value, unit] =
    abs < 60 * 60 * 1000
      ? [Math.max(1, Math.round(abs / 60000)), 'minute']
      : abs < DAY_MS
        ? [Math.round(abs / (60 * 60 * 1000)), 'hour']
        : [Math.round(abs / DAY_MS), 'day']
  const text = `${value} ${unit}${value === 1 ? '' : 's'}`
  return diff < 0 ? `${text} ago` : `in ${text}`
}

const isFailed = (state: CertificateRequestState) => state === 'Failed' || state === 'Denied'

const isExpiring = (certificate: ClusterCertificate) =>
  !!certificate.notAfter && time(certificate.notAfter) - Date.now() < EXPIRY_WARNING_DAYS * DAY_MS

const certificateKey = (environment: string, name: string) => `${environment}/${name}`

// The cert-manager Certificates and CertificateRequests in this TMA's cluster, opened from
// an issuer on the Certificates page (?issuer=<name>) or for all issuers. Read live from
// the cluster; "Renew" makes cert-manager issue a certificate again right away.
const CertificateRequests: React.FC = () => {
  const { fetchApi } = useApi()
  const roles = useContext(AuthRoleContext)
  const canRenew = !!roles?.isWriter || !!roles?.isAdmin
  const [searchParams, setSearchParams] = useSearchParams()
  const issuerFilter = searchParams.get('issuer') ?? ''
  const certificateFilter = searchParams.get('certificate') ?? ''

  const [overview, setOverview] = useState<CertificateOverview | null>(null)
  const [catalog, setCatalog] = useState<CertificateIssuer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [view, setView] = useState<View>('requests')
  const [requestFilter, setRequestFilter] = useState<RequestFilter>('all')
  const [certFilter, setCertFilter] = useState<CertificateFilter>('all')
  const [sort, setSort] = useState<Sort>('newest')
  const [query, setQuery] = useState('')

  const [renewing, setRenewing] = useState<ClusterCertificate | null>(null)
  const [renewError, setRenewError] = useState<string | null>(null)
  const [isRenewing, setIsRenewing] = useState(false)

  const load = useCallback(async () => {
    try {
      const [data, issuers] = await Promise.all([
        fetchApi({ endpoint: '/api/certificates', method: 'GET' }),
        // Only for display names; the page works without it.
        fetchApi({ endpoint: '/api/certificate-issuers', method: 'GET' }).catch(() => [])
      ])
      if (data === undefined) return // no token yet; the effect runs again once there is one
      setOverview(data)
      setCatalog(Array.isArray(issuers) ? issuers : [])
      setLoadError(null)
    } catch (error: any) {
      setLoadError(apiError(error, 'The certificates could not be read from the cluster.'))
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [fetchApi])

  useEffect(() => {
    load()
  }, [load])

  const refresh = () => {
    setIsRefreshing(true)
    load()
  }

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const displayName = useCallback(
    (issuer?: string | null) => catalog.find((i) => i.name === issuer)?.displayName ?? issuer ?? '',
    [catalog]
  )

  const issuerOptions = useMemo(() => {
    const names = new Set<string>(catalog.map((i) => i.name))
    overview?.certificates.forEach((c) => c.issuer && names.add(c.issuer))
    overview?.requests.forEach((r) => r.issuer && names.add(r.issuer))
    if (issuerFilter) names.add(issuerFilter)
    return [
      { value: ALL_ISSUERS, label: 'All issuers' },
      ...Array.from(names)
        .sort()
        .map((name) => ({ value: name, label: displayName(name) === name ? name : `${displayName(name)} (${name})` }))
    ]
  }, [catalog, overview, issuerFilter, displayName])

  const matchesQuery = useCallback(
    (values: (string | null | undefined)[]) => {
      const q = query.trim().toLowerCase()
      return !q || values.some((v) => !!v && v.toLowerCase().includes(q))
    },
    [query]
  )

  // Requests of the chosen issuer (and certificate), before the status filter, so the
  // chips can show counts.
  const scopedRequests = useMemo(
    () =>
      (overview?.requests ?? []).filter(
        (r) =>
          (!issuerFilter || r.issuer === issuerFilter) &&
          (!certificateFilter || certificateKey(r.environment, r.certificateName ?? '') === certificateFilter) &&
          matchesQuery([r.name, r.certificateName, r.tenant?.tenantName, r.environment, r.message, r.challengeMessage, ...r.dnsNames])
      ),
    [overview, issuerFilter, certificateFilter, matchesQuery]
  )

  const scopedCertificates = useMemo(
    () =>
      (overview?.certificates ?? []).filter(
        (c) =>
          (!issuerFilter || c.issuer === issuerFilter) &&
          matchesQuery([c.name, c.tenant?.tenantName, c.environment, c.message, ...c.dnsNames])
      ),
    [overview, issuerFilter, matchesQuery]
  )

  const requestCounts = useMemo(
    () => ({
      all: scopedRequests.length,
      failed: scopedRequests.filter((r) => isFailed(r.state)).length,
      pending: scopedRequests.filter((r) => r.state === 'Pending').length,
      issued: scopedRequests.filter((r) => r.state === 'Issued').length
    }),
    [scopedRequests]
  )

  const certificateCounts = useMemo(
    () => ({
      all: scopedCertificates.length,
      notReady: scopedCertificates.filter((c) => !c.ready).length,
      expiring: scopedCertificates.filter(isExpiring).length
    }),
    [scopedCertificates]
  )

  const visibleRequests = useMemo(() => {
    const filtered = scopedRequests.filter(
      (r) =>
        requestFilter === 'all' ||
        (requestFilter === 'failed' && isFailed(r.state)) ||
        (requestFilter === 'pending' && r.state === 'Pending') ||
        (requestFilter === 'issued' && r.state === 'Issued')
    )
    const direction = sort === 'newest' ? -1 : 1
    return [...filtered].sort((a, b) => direction * (time(a.createdAt) - time(b.createdAt)))
  }, [scopedRequests, requestFilter, sort])

  // Newest first means the certificates that expire last first; oldest first, the ones
  // that expire soonest.
  const visibleCertificates = useMemo(() => {
    const filtered = scopedCertificates.filter(
      (c) => certFilter === 'all' || (certFilter === 'notReady' && !c.ready) || (certFilter === 'expiring' && isExpiring(c))
    )
    const direction = sort === 'newest' ? -1 : 1
    return [...filtered].sort((a, b) => direction * (time(a.notAfter) - time(b.notAfter)))
  }, [scopedCertificates, certFilter, sort])

  const showHistory = (certificate: ClusterCertificate) => {
    const next = new URLSearchParams(searchParams)
    next.set('certificate', certificateKey(certificate.environment, certificate.name))
    setSearchParams(next, { replace: true })
    setRequestFilter('all')
    setSort('newest')
    setView('requests')
  }

  const confirmRenew = async () => {
    if (!renewing) return
    setIsRenewing(true)
    setRenewError(null)
    try {
      await fetchApi({
        endpoint: `/api/certificates/${encodeURIComponent(renewing.environment)}/${encodeURIComponent(renewing.name)}/renew`,
        method: 'POST'
      })
      customToast.success(`Renewal of ${renewing.dnsNames[0] ?? renewing.name} started`)
      setRenewing(null)
      await load()
    } catch (error: any) {
      setRenewError(apiError(error, 'The renewal could not be started.'))
    } finally {
      setIsRenewing(false)
    }
  }

  const renderTenant = (tenant?: CertificateTenantRef | null, environment?: string) => (
    <>
      {tenant ? (
        <Link
          to={`/subscriptions/${tenant.subscriptionId}/tenant/${tenant.tenantId}`}
          style={{ color: 'var(--tma-accent)', fontWeight: 600, textDecoration: 'none' }}
        >
          {tenant.tenantName}
        </Link>
      ) : (
        <span style={{ color: 'var(--tma-text-muted)' }}>Unknown</span>
      )}
      <div style={mutedStyle}>{environment}</div>
    </>
  )

  const renderHosts = (dnsNames: string[], name?: string | null) => (
    <>
      <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{dnsNames[0] ?? name}</div>
      {dnsNames.length > 1 && <div style={mutedStyle}>{`+ ${dnsNames.slice(1).join(', ')}`}</div>}
      {name && <div style={monoStyle}>{name}</div>}
    </>
  )

  const renderRequestRow = (request: ClusterCertificateRequest) => {
    const state = REQUEST_STATE[request.state] ?? REQUEST_STATE.Pending
    const detail = request.state === 'Issued' ? null : request.challengeMessage || request.message
    return (
      <tr key={`${request.environment}/${request.name}`}>
        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
          <span data-tooltip-id='global-tooltip' data-tooltip-content={formatDateTime(request.createdAt)}>
            {relative(request.createdAt)}
          </span>
          <div style={mutedStyle}>{request.createdAt ? new Date(request.createdAt).toLocaleDateString() : ''}</div>
        </td>
        <td style={tdStyle}>{renderHosts(request.dnsNames, request.certificateName)}</td>
        <td style={tdStyle}>{renderTenant(request.tenant, request.environment)}</td>
        <td style={tdStyle}>
          <div>{displayName(request.issuer)}</div>
          {request.revision != null && <div style={mutedStyle}>{`Revision ${request.revision}`}</div>}
        </td>
        <td style={{ ...tdStyle, maxWidth: '460px' }}>
          <span style={pill(state.color)}>{state.label}</span>
          {detail && <div style={{ ...mutedStyle, marginTop: '6px', wordBreak: 'break-word' }}>{detail}</div>}
          {request.failureTime && <div style={{ ...mutedStyle, marginTop: '2px' }}>{`Failed ${formatDateTime(request.failureTime)}`}</div>}
        </td>
      </tr>
    )
  }

  const renderCertificateStatus = (certificate: ClusterCertificate) => {
    if (certificate.issuing)
      return (
        <span style={pill('var(--tma-accent)')} data-tooltip-id='global-tooltip' data-tooltip-content={certificate.message ?? undefined}>
          Issuing
        </span>
      )
    return (
      <span
        style={pill(certificate.ready ? 'var(--tma-status-ok-strong)' : 'var(--tma-danger-text)')}
        data-tooltip-id='global-tooltip'
        data-tooltip-content={certificate.message ?? undefined}
      >
        {certificate.ready ? 'Ready' : 'Not ready'}
      </span>
    )
  }

  const renderCertificateRow = (certificate: ClusterCertificate) => {
    const expiring = isExpiring(certificate)
    return (
      <tr key={certificateKey(certificate.environment, certificate.name)}>
        <td style={tdStyle}>{renderHosts(certificate.dnsNames, certificate.name)}</td>
        <td style={tdStyle}>{renderTenant(certificate.tenant, certificate.environment)}</td>
        <td style={tdStyle}>{displayName(certificate.issuer)}</td>
        <td style={tdStyle}>
          {renderCertificateStatus(certificate)}
          {!certificate.ready && certificate.message && (
            <div style={{ ...mutedStyle, marginTop: '6px', maxWidth: '320px', wordBreak: 'break-word' }}>{certificate.message}</div>
          )}
        </td>
        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
          {certificate.notAfter ? (
            <>
              <span
                style={{ color: expiring ? 'var(--tma-danger-text)' : undefined, fontWeight: expiring ? 600 : undefined }}
                data-tooltip-id='global-tooltip'
                data-tooltip-content={formatDateTime(certificate.notAfter)}
              >
                {new Date(certificate.notAfter).toLocaleDateString()}
              </span>
              <div style={mutedStyle}>{relative(certificate.notAfter)}</div>
            </>
          ) : (
            <span style={{ color: 'var(--tma-text-muted)' }}>Not issued</span>
          )}
        </td>
        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
          {certificate.renewalTime ? (
            <span data-tooltip-id='global-tooltip' data-tooltip-content={formatDateTime(certificate.renewalTime)}>
              {relative(certificate.renewalTime)}
            </span>
          ) : (
            <span style={{ color: 'var(--tma-text-muted)' }}>-</span>
          )}
        </td>
        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'inline-flex', gap: '6px' }}>
            <Button2
              text='Requests'
              variant='subtle'
              leftElement={<Icon iconName='TimeClockCircle' size={14} />}
              buttonSize='30px'
              buttonRadius='8px'
              type='button'
              tooltip='Show the requests of this certificate'
              onClick={() => showHistory(certificate)}
            />
            {canRenew && (
              <Button2
                text='Renew'
                variant='subtle'
                isDisabled={certificate.issuing}
                leftElement={<Icon iconName='SynchronizeArrows1' size={14} />}
                buttonSize='30px'
                buttonRadius='8px'
                type='button'
                tooltip={certificate.issuing ? 'cert-manager is already issuing it' : 'Issue this certificate again now'}
                onClick={() => {
                  setRenewError(null)
                  setRenewing(certificate)
                }}
              />
            )}
          </div>
        </td>
      </tr>
    )
  }

  const renderTable = () => {
    if (view === 'requests') {
      if (visibleRequests.length === 0)
        return (
          <div style={{ padding: '40px 16px', textAlign: 'center' }}>
            <Text textStyle='description' textColor='grey' content='No requests match these filters.' />
            <div style={{ ...mutedStyle, marginTop: '6px' }}>cert-manager only keeps the most recent requests of each certificate.</div>
          </div>
        )
      return (
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              <th style={thStyle}>Requested</th>
              <th style={{ ...thStyle, minWidth: '220px' }}>Certificate</th>
              <th style={thStyle}>Tenant</th>
              <th style={thStyle}>Issuer</th>
              <th style={{ ...thStyle, minWidth: '240px' }}>Status</th>
            </tr>
          </thead>
          <tbody>{visibleRequests.map(renderRequestRow)}</tbody>
        </table>
      )
    }
    if (visibleCertificates.length === 0)
      return (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <Text textStyle='description' textColor='grey' content='No certificates match these filters.' />
        </div>
      )
    return (
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, minWidth: '220px' }}>Certificate</th>
            <th style={thStyle}>Tenant</th>
            <th style={thStyle}>Issuer</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Expires</th>
            <th style={thStyle}>Renews</th>
            <th style={{ ...thStyle, textAlign: 'right' }} aria-label='Actions' />
          </tr>
        </thead>
        <tbody>{visibleCertificates.map(renderCertificateRow)}</tbody>
      </table>
    )
  }

  const renderBody = () => {
    if (roles === null || isLoading) return <LoadingState text='Reading certificates from the cluster…' />
    if (loadError)
      return (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={loadError} />
        </div>
      )
    if (overview && !overview.clusterEnabled)
      return (
        <div style={{ paddingTop: '60px', display: 'flex', justifyContent: 'center' }}>
          <EmptyState
            imageName='security'
            size='medium'
            title='Cluster sync is off'
            text='This environment does not read from a cluster, so there are no certificates to show.'
          />
        </div>
      )
    return (
      <>
        {!!overview?.errors.length && (
          <div
            style={{
              margin: '12px 16px 0',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--tma-danger-border)',
              background: 'var(--tma-danger-tint)'
            }}
          >
            <Text
              textStyle='description'
              style={{ color: 'var(--tma-danger-text)' }}
              content={`Some stages could not be read: ${overview.errors.join('; ')}`}
            />
          </div>
        )}
        {renderTable()}
      </>
    )
  }

  const requestChips: { key: RequestFilter; label: string }[] = [
    { key: 'all', label: `All ${requestCounts.all}` },
    { key: 'failed', label: `Failed ${requestCounts.failed}` },
    { key: 'pending', label: `Pending ${requestCounts.pending}` },
    { key: 'issued', label: `Issued ${requestCounts.issued}` }
  ]
  const certificateChips: { key: CertificateFilter; label: string }[] = [
    { key: 'all', label: `All ${certificateCounts.all}` },
    { key: 'notReady', label: `Not ready ${certificateCounts.notReady}` },
    { key: 'expiring', label: `Expires within ${EXPIRY_WARNING_DAYS} days ${certificateCounts.expiring}` }
  ]
  const sortLabel =
    view === 'requests' ? (sort === 'newest' ? 'Newest first' : 'Oldest first') : sort === 'newest' ? 'Expires last first' : 'Expires first'

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'var(--app-height, 100vh)',
        overflow: 'hidden',
        background: 'var(--tma-panel-bg)',
        fontFamily: APP_FONT
      }}
    >
      <Toast />
      <div style={bandStyle}>
        <BandBackButton to='/certificates' label='Back to certificates' />
        <span style={{ color: 'var(--tma-band-text)', fontSize: '14px', fontWeight: 600, fontFamily: APP_FONT }}>
          {issuerFilter ? displayName(issuerFilter) : 'All issuers'}
        </span>
        <span style={{ color: 'var(--tma-band-text-muted)', fontSize: '12px' }}>
          {`Certificates and requests${overview?.clusterName ? ` in ${overview.clusterName}` : ''}`}
        </span>
      </div>

      <div style={toolbarStyle}>
        <div style={{ display: 'inline-flex', gap: '6px' }} role='tablist'>
          {(['requests', 'certificates'] as View[]).map((v) => (
            <button key={v} type='button' role='tab' aria-selected={view === v} style={chip(view === v)} onClick={() => setView(v)}>
              {v === 'requests' ? 'Requests' : 'Certificates'}
            </button>
          ))}
        </div>
        <div className='search-no-glow' style={{ width: '240px' }}>
          <TextField
            isSearchMode
            placeholder='Search hostname, tenant, message'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            type='text'
            variant='default'
          />
        </div>
        <div style={{ width: '240px' }}>
          <Autocomplete
            value={issuerFilter || ALL_ISSUERS}
            onChange={(e: { value: string }) => setParam('issuer', e.value === ALL_ISSUERS ? '' : e.value)}
            options={issuerOptions}
          />
        </div>
        <button
          type='button'
          style={chip(false)}
          onClick={() => setSort(sort === 'newest' ? 'oldest' : 'newest')}
          aria-label={`Sort: ${sortLabel}`}
        >
          <Icon iconName={sort === 'newest' ? 'ArrowDown1' : 'ArrowUp1'} size={10} color='var(--tma-text-muted)' />
          {sortLabel}
        </button>
        <div style={{ flex: 1 }} />
        <Button2
          text={isRefreshing ? 'Refreshing…' : 'Refresh'}
          variant='subtle'
          isDisabled={isRefreshing || isLoading}
          leftElement={<Icon iconName='SynchronizeArrows1' size={14} />}
          buttonSize='32px'
          buttonRadius='8px'
          type='button'
          onClick={refresh}
        />
      </div>

      <div style={{ ...toolbarStyle, paddingTop: '8px', paddingBottom: '8px' }}>
        {view === 'requests'
          ? requestChips.map((c) => (
              <button
                key={c.key}
                type='button'
                aria-pressed={requestFilter === c.key}
                style={chip(requestFilter === c.key)}
                onClick={() => setRequestFilter(c.key)}
              >
                {c.label}
              </button>
            ))
          : certificateChips.map((c) => (
              <button
                key={c.key}
                type='button'
                aria-pressed={certFilter === c.key}
                style={chip(certFilter === c.key)}
                onClick={() => setCertFilter(c.key)}
              >
                {c.label}
              </button>
            ))}
        {view === 'requests' && certificateFilter && (
          <button
            type='button'
            style={chip(true)}
            onClick={() => setParam('certificate', '')}
            aria-label='Show the requests of every certificate'
          >
            {`Certificate ${certificateFilter}`}
            <Icon iconName='Close' size={10} color='var(--tma-accent)' />
          </button>
        )}
      </div>

      <div className='custom-scrollbar' style={{ flex: 1, overflow: 'auto' }}>
        {renderBody()}
      </div>

      <Modal
        isOpen={!!renewing}
        width='45%'
        height='auto'
        onClose={() => !isRenewing && setRenewing(null)}
        onCancel={() => !isRenewing && setRenewing(null)}
        hasHeaderBorder={true}
        headerTitle={`Renew ${renewing?.dnsNames[0] ?? renewing?.name ?? ''}`}
        onConfirm={confirmRenew}
        isConfirmDisabled={isRenewing}
        variant='prompt'
        labelConfirm={isRenewing ? 'Renewing…' : 'Renew now'}
        labelCancel='Cancel'
      >
        <Text
          content={`cert-manager requests a new certificate from ${displayName(renewing?.issuer) || 'the issuer'} right away. The current one stays in use until the new one is issued. Each renewal counts against the CA's rate limits (Let's Encrypt allows 5 identical certificates a week), so don't repeat it to retry a failure: fix the cause first.`}
        />
        {renewError && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--tma-danger-border)',
              background: 'var(--tma-danger-tint)'
            }}
          >
            <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={renewError} />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default CertificateRequests
